import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';

interface UserInfo {
  username: string;
  name: string;
  email: string;
  roles: string[];
}

export interface AuthRequestConfig {
  scopes: string[];
  prompt?: string;
}

interface AuthProviderProps {
  children: React.ReactNode;
  loginRequest: AuthRequestConfig;
  apiRequest: AuthRequestConfig;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserInfo | null;
  login: () => void;
  logout: () => void;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  loginRequest,
  apiRequest,
}) => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<UserInfo | null>(null);
  const navigate = useNavigate();

  // Set user details when authenticated
  useEffect(() => {
    if (accounts.length > 0) {
      const account = accounts[0];
      const claims = account.idTokenClaims ?? {};

      setUser({
        username: account.username,
        name: (claims.name as string) || account.name || '',
        email: account.username,
        roles: (claims.roles as string[]) || [],
      });
      console.log('user', user);
    } else {
      setUser(null);
    }
  }, [accounts]);

  // Redirect login
  const login = () => {
    localStorage.setItem('isAuthenticated', 'true');

    instance
      .loginRedirect({
        ...loginRequest,
        prompt: loginRequest.prompt ?? 'login',
      })
      .then(() => {
        // Prevent navigating back to login
        history.replaceState(null, '', '/ops/partners');
      });
  };

  // Logout
  const logout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('token');
    instance.clearCache();

    instance.logoutRedirect({
      onRedirectNavigate: () => false,
    });

    navigate('/', { replace: true });
  };

  // Acquire token for API usage
  const getToken = async () => {
    if (accounts.length === 0) return null;

    try {
      const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account: accounts[0],
      });

      localStorage.setItem('token', response.accessToken);
      console.log('Microsoft Entra token:', response.accessToken);

      return response.accessToken;
    } catch {
      await instance.acquireTokenRedirect(apiRequest);
      return null;
    }
  };

  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      user,
      login,
      logout,
      getToken,
    }),
    [isAuthenticated, user]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return ctx;
};
